import React, { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { AnalyticsApi } from '../../services/api/AnalyticsApi';
import {
  MetricCard,
  LoadingChart,
  EmptyState,
  DateRangePicker,
  ExportButton,
  InsightCard
} from '../../components/analytics/SharedAnalyticsComponents';
import PdfExportButton from '../../components/analytics/PdfExportButton';
import { ReportGenerator } from '../../utils/ReportGenerator';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const COLORS = ['#007bff', '#28a745', '#ffc107', '#dc3545', '#17a2b8', '#6f42c1', '#20c997', '#fd7e14'];

/**
 * AnalyticsToolkit — Unified page for Funnel Analysis, Cohort Retention, and Journey Patterns.
 * Wires: analyzeFunnel, getSavedFunnels, saveFunnel, getCohortRetention, getJourneyPatterns
 */
export default function AnalyticsToolkit() {
  const { user } = useAuth();
  const isAdmin = user?.roles?.some(r => r.roleName === 'admin');
  const [activeTab, setActiveTab] = useState('funnel');

  if (!isAdmin) {
    return (
      <div className="settings-container">
        <h2>Analytics Toolkit</h2>
        <EmptyState message="Admin access required" icon="🔒" />
      </div>
    );
  }

  const tabs = [
    { id: 'funnel', label: '🔁 Funnel Analysis' },
    { id: 'cohort', label: '📊 Cohort Retention' },
    { id: 'journey', label: '🗺️ Journey Patterns' }
  ];

  return (
    <div className="settings-container">
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ margin: 0 }}>🧰 Analytics Toolkit</h2>
        <p style={{ fontSize: '.85rem', color: 'var(--text-muted)', margin: '.25rem 0 0 0' }}>
          Funnel analysis, cohort retention, and user journey exploration
        </p>
      </div>

      {/* Tab Bar */}
      <div style={{ display: 'flex', gap: '.5rem', marginBottom: '1.5rem', borderBottom: '2px solid var(--border-light)', paddingBottom: '.5rem' }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '.5rem 1rem',
              background: activeTab === tab.id ? '#007bff' : 'transparent',
              color: activeTab === tab.id ? '#fff' : 'inherit',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: activeTab === tab.id ? 600 : 400,
              fontSize: '.85rem'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'funnel' && <FunnelTab />}
      {activeTab === 'cohort' && <CohortTab />}
      {activeTab === 'journey' && <JourneyTab />}
    </div>
  );
}

// ─── Funnel Analysis Tab ────────────────────────────────────────────────

function FunnelTab() {
  const [dateRange, setDateRange] = useState(() => ({
    from: new Date(Date.now() - 30 * 86400000).toISOString(),
    to: new Date().toISOString()
  }));
  const [steps, setSteps] = useState([
    { eventType: 'DOCUMENT_VIEW', category: '', action: '' },
    { eventType: 'QUIZ_STARTED', category: '', action: '' }
  ]);
  const [sessionBased, setSessionBased] = useState(true);
  const [result, setResult] = useState(null);
  const [savedFunnels, setSavedFunnels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [funnelName, setFunnelName] = useState('');

  // Load saved funnels on mount
  useEffect(() => {
    AnalyticsApi.getSavedFunnels()
      .then(data => setSavedFunnels(Array.isArray(data) ? data : []))
      .catch(() => {}); // silent — may 404 if no funnels saved yet
  }, []);

  const runAnalysis = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await AnalyticsApi.analyzeFunnel({
        steps: steps.filter(s => s.eventType),
        from: dateRange.from,
        to: dateRange.to,
        sessionBased
      });
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [steps, dateRange, sessionBased]);

  const addStep = () => {
    if (steps.length < 8) {
      setSteps(prev => [...prev, { eventType: '', category: '', action: '' }]);
    }
  };

  const removeStep = (index) => {
    if (steps.length > 2) {
      setSteps(prev => prev.filter((_, i) => i !== index));
    }
  };

  const updateStep = (index, field, value) => {
    setSteps(prev => prev.map((s, i) => i === index ? { ...s, [field]: value } : s));
  };

  const saveFunnel = async () => {
    if (!funnelName.trim()) return;
    try {
      await AnalyticsApi.saveFunnel({ name: funnelName, steps, sessionBased });
      const updated = await AnalyticsApi.getSavedFunnels();
      setSavedFunnels(Array.isArray(updated) ? updated : []);
      setFunnelName('');
    } catch (err) {
      setError('Failed to save funnel: ' + err.message);
    }
  };

  const loadSavedFunnel = (funnel) => {
    if (funnel.steps) setSteps(funnel.steps);
    if (funnel.sessionBased !== undefined) setSessionBased(funnel.sessionBased);
  };

  // Extract funnel steps from result for chart
  const chartData = result?.steps ? result.steps.map((step, idx) => ({
    name: step.eventType || `Step ${idx + 1}`,
    users: step.userCount || 0,
    conversionRate: step.conversionRate != null ? (step.conversionRate * 100).toFixed(1) : null
  })) : [];

  return (
    <div>
      {/* Controls */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '.5rem' }}>
        <DateRangePicker from={dateRange.from} to={dateRange.to} onChange={setDateRange} />
        <div style={{ display: 'flex', gap: '.5rem', alignItems: 'center' }}>
          <label style={{ fontSize: '.8rem' }}>
            <input type="checkbox" checked={sessionBased} onChange={e => setSessionBased(e.target.checked)} />
            {' '}Session-based
          </label>
          <button onClick={runAnalysis} disabled={loading || steps.filter(s => s.eventType).length < 2}>
            {loading ? '⏳ Analyzing...' : '▶️ Run Funnel'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ padding: '.75rem', background: '#ffebee', border: '1px solid #f44336', borderRadius: '8px', color: '#b71c1c', marginBottom: '1rem', fontSize: '.8rem' }}>
          {error}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        {/* Step Builder */}
        <div style={{ background: 'var(--card-bg)', padding: '1rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
          <h3 style={{ marginTop: 0, fontSize: '.95rem' }}>Funnel Steps</h3>
          {steps.map((step, idx) => (
            <div key={idx} style={{ display: 'flex', gap: '.5rem', marginBottom: '.5rem', alignItems: 'center' }}>
              <span style={{ fontSize: '.75rem', fontWeight: 700, minWidth: '24px' }}>#{idx + 1}</span>
              <input
                placeholder="Event type (e.g. DOCUMENT_OPENED)"
                value={step.eventType}
                onChange={e => updateStep(idx, 'eventType', e.target.value)}
                style={{ flex: 2, fontSize: '.8rem' }}
              />
              <input
                placeholder="Category (optional)"
                value={step.category}
                onChange={e => updateStep(idx, 'category', e.target.value)}
                style={{ flex: 1, fontSize: '.8rem' }}
              />
              {steps.length > 2 && (
                <button onClick={() => removeStep(idx)} style={{ padding: '.25rem .5rem', fontSize: '.75rem' }}>✕</button>
              )}
            </div>
          ))}
          <div style={{ display: 'flex', gap: '.5rem', marginTop: '.5rem' }}>
            <button className="secondary" onClick={addStep} disabled={steps.length >= 8} style={{ fontSize: '.8rem' }}>
              + Add Step
            </button>
          </div>

          {/* Save */}
          <div style={{ marginTop: '1rem', display: 'flex', gap: '.5rem' }}>
            <input
              placeholder="Funnel name..."
              value={funnelName}
              onChange={e => setFunnelName(e.target.value)}
              style={{ flex: 1, fontSize: '.8rem' }}
            />
            <button className="secondary" onClick={saveFunnel} disabled={!funnelName.trim()} style={{ fontSize: '.8rem' }}>
              💾 Save
            </button>
          </div>

          {/* Saved Funnels */}
          {savedFunnels.length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <h4 style={{ fontSize: '.8rem', marginBottom: '.5rem' }}>Saved Funnels</h4>
              {savedFunnels.map((f, idx) => (
                <button
                  key={idx}
                  className="secondary"
                  onClick={() => loadSavedFunnel(f)}
                  style={{ fontSize: '.75rem', marginRight: '.25rem', marginBottom: '.25rem' }}
                >
                  {f.name || `Funnel ${idx + 1}`}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Results Chart */}
        <div style={{ background: 'var(--card-bg)', padding: '1rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0, fontSize: '.95rem' }}>Funnel Results</h3>
            <div style={{ display: 'flex', gap: '.5rem' }}>
              {chartData.length > 0 && <ExportButton data={chartData} filename="funnel-analysis" />}
              {chartData.length > 0 && <PdfExportButton onGenerate={async () => {
                const report = new ReportGenerator('Funnel Analysis Report');
                report.addMetrics(chartData.map(d => ({ label: d.step || d.name, value: String(d.users || d.count || 0) })));
                if (result?.overallConversionRate != null) {
                  report.addText(`Overall conversion rate: ${(result.overallConversionRate * 100).toFixed(1)}%`);
                }
                report.addSectionHeading('Funnel Steps');
                report.addTable(
                  ['Step', 'Users', 'Drop-off %'],
                  chartData.map(d => [d.step || d.name, d.users || d.count || 0, d.dropOff != null ? `${(d.dropOff * 100).toFixed(1)}%` : '—'])
                );
                report.download('funnel-analysis');
              }} />}
            </div>
          </div>

          {loading ? (
            <LoadingChart height={300} />
          ) : chartData.length === 0 ? (
            <EmptyState message="Configure steps and run the funnel analysis" icon="🔁" />
          ) : (
            <>
              {/* Overall Conversion */}
              {result?.overallConversionRate != null && (
                <InsightCard
                  title="Overall Conversion"
                  description={`${(result.overallConversionRate * 100).toFixed(1)}% of users completed all ${chartData.length} steps`}
                  severity={result.overallConversionRate > 0.1 ? 'success' : 'warning'}
                />
              )}
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" style={{ fontSize: '.7rem' }} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={160}
                    style={{ fontSize: '.7rem' }}
                    tickFormatter={v => v.length > 20 ? v.slice(0, 20) + '…' : v}
                  />
                  <Tooltip
                    formatter={(value, name) => [value.toLocaleString(), name]}
                    contentStyle={{ fontSize: '.75rem' }}
                  />
                  <Legend wrapperStyle={{ fontSize: '.75rem' }} />
                  <Bar dataKey="users" fill="#007bff" name="Users" />
                </BarChart>
              </ResponsiveContainer>
              {/* Step-by-step table */}
              <div style={{ overflowX: 'auto', marginTop: '1rem' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.75rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--border-light)' }}>
                      <th style={{ textAlign: 'left', padding: '.4rem' }}>Step</th>
                      <th style={{ textAlign: 'right', padding: '.4rem' }}>Users</th>
                      <th style={{ textAlign: 'right', padding: '.4rem' }}>Drop-off</th>
                      <th style={{ textAlign: 'right', padding: '.4rem' }}>Conversion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chartData.map((step, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid var(--border-light)' }}>
                        <td style={{ padding: '.4rem' }}>{step.name}</td>
                        <td style={{ padding: '.4rem', textAlign: 'right' }}>{step.users.toLocaleString()}</td>
                        <td style={{ padding: '.4rem', textAlign: 'right' }}>
                          {idx > 0 ? `-${(chartData[idx - 1].users - step.users).toLocaleString()}` : '—'}
                        </td>
                        <td style={{ padding: '.4rem', textAlign: 'right' }}>
                          {step.conversionRate != null ? `${step.conversionRate}%` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Cohort Retention Tab ───────────────────────────────────────────────

function CohortTab() {
  const [dateRange, setDateRange] = useState(() => ({
    from: new Date(Date.now() - 90 * 86400000).toISOString(),
    to: new Date().toISOString()
  }));
  const [cohortType, setCohortType] = useState('SIGNUP');
  const [period, setPeriod] = useState('WEEKLY');
  const [returnMetric, setReturnMetric] = useState('ANY_ACTIVITY');
  const [maxPeriods, setMaxPeriods] = useState(8);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const runAnalysis = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await AnalyticsApi.getCohortRetention({
        cohortType,
        period,
        returnMetric,
        from: dateRange.from,
        to: dateRange.to,
        maxPeriods
      });
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [cohortType, period, returnMetric, dateRange, maxPeriods]);

  const getRetentionColor = (rate) => {
    if (rate == null) return 'transparent';
    const pct = rate * 100;
    if (pct >= 50) return '#28a745';
    if (pct >= 30) return '#ffc107';
    if (pct >= 10) return '#fd7e14';
    return '#dc3545';
  };

  const cohorts = result?.cohorts || [];

  return (
    <div>
      {/* Controls */}
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '1rem' }}>
        <DateRangePicker from={dateRange.from} to={dateRange.to} onChange={setDateRange} />

        <div>
          <label style={{ display: 'block', fontSize: '.75rem', fontWeight: 600, marginBottom: '.25rem' }}>Cohort Type</label>
          <select value={cohortType} onChange={e => setCohortType(e.target.value)} style={{ fontSize: '.8rem' }}>
            <option value="SIGNUP">Sign Up</option>
            <option value="FIRST_LOGIN">First Login</option>
            <option value="FIRST_PURCHASE">First Purchase</option>
            <option value="FIRST_COURSE">First Course</option>
          </select>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '.75rem', fontWeight: 600, marginBottom: '.25rem' }}>Period</label>
          <select value={period} onChange={e => setPeriod(e.target.value)} style={{ fontSize: '.8rem' }}>
            <option value="DAILY">Daily</option>
            <option value="WEEKLY">Weekly</option>
            <option value="MONTHLY">Monthly</option>
          </select>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '.75rem', fontWeight: 600, marginBottom: '.25rem' }}>Return Metric</label>
          <select value={returnMetric} onChange={e => setReturnMetric(e.target.value)} style={{ fontSize: '.8rem' }}>
            <option value="ANY_ACTIVITY">Any Activity</option>
            <option value="LOGIN">Login</option>
            <option value="ENGAGEMENT">Engagement</option>
            <option value="PURCHASE">Purchase</option>
          </select>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '.75rem', fontWeight: 600, marginBottom: '.25rem' }}>Max Periods</label>
          <input
            type="number"
            value={maxPeriods}
            onChange={e => setMaxPeriods(parseInt(e.target.value) || 8)}
            min={2}
            max={24}
            style={{ width: '60px', fontSize: '.8rem' }}
          />
        </div>

        <button onClick={runAnalysis} disabled={loading}>
          {loading ? '⏳ Analyzing...' : '▶️ Run Cohort'}
        </button>
      </div>

      {error && (
        <div style={{ padding: '.75rem', background: '#ffebee', border: '1px solid #f44336', borderRadius: '8px', color: '#b71c1c', marginBottom: '1rem', fontSize: '.8rem' }}>
          {error}
        </div>
      )}

      {/* Summary KPIs */}
      {result && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          <MetricCard
            label="Total Cohorts"
            value={cohorts.length}
            color="#007bff"
          />
          <MetricCard
            label="Total Users"
            value={(cohorts.reduce((s, c) => s + (c.cohortSize || 0), 0)).toLocaleString()}
            color="#28a745"
          />
          {result.averageRetention != null && (
            <MetricCard
              label="Avg Retention (Period 1)"
              value={`${(result.averageRetention * 100).toFixed(1)}%`}
              color="#17a2b8"
            />
          )}
        </div>
      )}

      {/* Retention Matrix */}
      <div style={{ background: 'var(--card-bg)', padding: '1rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0, fontSize: '.95rem' }}>Retention Matrix</h3>
          <div style={{ display: 'flex', gap: '.5rem' }}>
            {cohorts.length > 0 && <ExportButton data={cohorts} filename="cohort-retention" />}
            {cohorts.length > 0 && <PdfExportButton onGenerate={async () => {
              const report = new ReportGenerator('Cohort Retention Report');
              if (result?.averageRetention != null) {
                report.addMetrics([{ label: 'Avg Retention (P1)', value: `${(result.averageRetention * 100).toFixed(1)}%` }]);
              }
              report.addSectionHeading('Retention Cohorts');
              const maxP = Math.min(maxPeriods || 6, 12);
              report.addTable(
                ['Cohort', 'Size', ...Array.from({ length: maxP }, (_, i) => `P${i}`)],
                cohorts.map(c => [
                  c.cohort || c.cohortWeek || '',
                  c.cohortSize || c.size || 0,
                  ...Array.from({ length: maxP }, (_, i) => {
                    const r = c.retentionByPeriod?.[i] ?? c[`period_${i}`];
                    return r != null ? `${(r * 100).toFixed(0)}%` : '—';
                  })
                ])
              );
              report.download('cohort-retention');
            }} />}
          </div>
        </div>

        {loading ? (
          <LoadingChart height={300} />
        ) : cohorts.length === 0 ? (
          <EmptyState message="Select parameters and run cohort analysis" icon="📊" />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.7rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border-light)' }}>
                  <th style={{ padding: '.4rem', textAlign: 'left', position: 'sticky', left: 0, background: 'var(--card-bg)' }}>Cohort</th>
                  <th style={{ padding: '.4rem', textAlign: 'right' }}>Size</th>
                  {Array.from({ length: Math.min(maxPeriods, 12) }, (_, i) => (
                    <th key={i} style={{ padding: '.4rem', textAlign: 'center' }}>P{i}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cohorts.map((cohort, cIdx) => (
                  <tr key={cIdx} style={{ borderBottom: '1px solid var(--border-light)' }}>
                    <td style={{ padding: '.4rem', whiteSpace: 'nowrap', position: 'sticky', left: 0, background: 'var(--card-bg)' }}>
                      {cohort.label || cohort.cohortDate || `Cohort ${cIdx + 1}`}
                    </td>
                    <td style={{ padding: '.4rem', textAlign: 'right' }}>{(cohort.cohortSize || 0).toLocaleString()}</td>
                    {Array.from({ length: Math.min(maxPeriods, 12) }, (_, pIdx) => {
                      const rates = cohort.retentionRates || cohort.retention || [];
                      const rate = rates[pIdx];
                      return (
                        <td key={pIdx} style={{
                          padding: '.4rem',
                          textAlign: 'center',
                          background: rate != null ? getRetentionColor(rate) + '33' : 'transparent',
                          color: rate != null ? getRetentionColor(rate) : 'var(--text-muted)',
                          fontWeight: rate != null && rate >= 0.3 ? 600 : 400
                        }}>
                          {rate != null ? `${(rate * 100).toFixed(0)}%` : '—'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Journey Patterns Tab ───────────────────────────────────────────────

function JourneyTab() {
  const [dateRange, setDateRange] = useState(() => ({
    from: new Date(Date.now() - 30 * 86400000).toISOString(),
    to: new Date().toISOString()
  }));
  const [minSupport, setMinSupport] = useState(5);
  const [maxSteps, setMaxSteps] = useState(4);
  const [category, setCategory] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const runAnalysis = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await AnalyticsApi.getJourneyPatterns({
        from: dateRange.from,
        to: dateRange.to,
        minSupport,
        maxSteps,
        category: category || undefined,
        includeAnonymous: false
      });
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [dateRange, minSupport, maxSteps, category]);

  const patterns = result?.patterns || result || [];
  const patternList = Array.isArray(patterns) ? patterns : [];

  return (
    <div>
      {/* Controls */}
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '1rem' }}>
        <DateRangePicker from={dateRange.from} to={dateRange.to} onChange={setDateRange} />

        <div>
          <label style={{ display: 'block', fontSize: '.75rem', fontWeight: 600, marginBottom: '.25rem' }}>Min Support</label>
          <input
            type="number"
            value={minSupport}
            onChange={e => setMinSupport(parseInt(e.target.value) || 5)}
            min={1}
            max={100}
            style={{ width: '70px', fontSize: '.8rem' }}
          />
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '.75rem', fontWeight: 600, marginBottom: '.25rem' }}>Max Steps</label>
          <input
            type="number"
            value={maxSteps}
            onChange={e => setMaxSteps(parseInt(e.target.value) || 4)}
            min={2}
            max={10}
            style={{ width: '60px', fontSize: '.8rem' }}
          />
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '.75rem', fontWeight: 600, marginBottom: '.25rem' }}>Category Filter</label>
          <input
            type="text"
            value={category}
            onChange={e => setCategory(e.target.value)}
            placeholder="e.g. study"
            style={{ width: '120px', fontSize: '.8rem' }}
          />
        </div>

        <button onClick={runAnalysis} disabled={loading}>
          {loading ? '⏳ Analyzing...' : '▶️ Find Patterns'}
        </button>
      </div>

      {error && (
        <div style={{ padding: '.75rem', background: '#ffebee', border: '1px solid #f44336', borderRadius: '8px', color: '#b71c1c', marginBottom: '1rem', fontSize: '.8rem' }}>
          {error}
        </div>
      )}

      {/* Summary */}
      {patternList.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          <MetricCard
            label="Patterns Found"
            value={patternList.length}
            color="#007bff"
          />
          <MetricCard
            label="Most Common"
            value={patternList[0]?.support?.toLocaleString() || patternList[0]?.count?.toLocaleString() || '—'}
            subtitle={patternList[0]?.pattern?.join(' → ') || patternList[0]?.steps?.join(' → ') || ''}
            color="#28a745"
          />
        </div>
      )}

      {/* Pattern Bar Chart */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
        <div style={{ background: 'var(--card-bg)', padding: '1rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0, fontSize: '.95rem' }}>Top Patterns by Frequency</h3>
            <div style={{ display: 'flex', gap: '.5rem' }}>
              {patternList.length > 0 && <ExportButton data={patternList} filename="journey-patterns" />}
              {patternList.length > 0 && <PdfExportButton onGenerate={async () => {
                const report = new ReportGenerator('Journey Patterns Report');
                report.addMetrics([
                  { label: 'Total Patterns', value: String(patternList.length) },
                  { label: 'Most Common', value: String(patternList[0]?.support || patternList[0]?.count || 0), sublabel: (patternList[0]?.pattern || patternList[0]?.steps || []).join(' → ') }
                ]);
                report.addSectionHeading('Journey Patterns');
                report.addTable(
                  ['Pattern', 'Support', 'Unique Users', 'Avg Duration'],
                  patternList.map(p => [
                    (p.pattern || p.steps || []).join(' → '),
                    p.support || p.count || 0,
                    p.uniqueUsers || p.users || 0,
                    p.avgDuration ? `${(p.avgDuration / 60).toFixed(1)}m` : '—'
                  ])
                );
                report.download('journey-patterns');
              }} />}
            </div>
          </div>

          {loading ? (
            <LoadingChart height={350} />
          ) : patternList.length === 0 ? (
            <EmptyState message="Configure parameters and find journey patterns" icon="🗺️" />
          ) : (
            <ResponsiveContainer width="100%" height={350}>
              <BarChart
                data={patternList.slice(0, 15).map((p, i) => ({
                  name: (p.pattern || p.steps || []).join(' → '),
                  support: p.support || p.count || 0,
                  users: p.uniqueUsers || p.users || 0
                }))}
                layout="vertical"
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" style={{ fontSize: '.7rem' }} />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={200}
                  style={{ fontSize: '.6rem' }}
                  tickFormatter={v => v.length > 30 ? v.slice(0, 30) + '…' : v}
                />
                <Tooltip contentStyle={{ fontSize: '.75rem' }} />
                <Legend wrapperStyle={{ fontSize: '.75rem' }} />
                <Bar dataKey="support" fill="#007bff" name="Occurrences" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Pattern Detail Table */}
        <div style={{ background: 'var(--card-bg)', padding: '1rem', borderRadius: '12px', boxShadow: 'var(--shadow-sm)', border: '1px solid var(--border-light)' }}>
          <h3 style={{ marginTop: 0, fontSize: '.95rem' }}>Pattern Details</h3>

          {loading ? (
            <LoadingChart height={350} />
          ) : patternList.length === 0 ? (
            <EmptyState message="No patterns found" />
          ) : (
            <div style={{ overflowX: 'auto', maxHeight: '400px', overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.7rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--border-light)', position: 'sticky', top: 0, background: 'var(--card-bg)' }}>
                    <th style={{ padding: '.4rem', textAlign: 'left' }}>#</th>
                    <th style={{ padding: '.4rem', textAlign: 'left' }}>Journey Path</th>
                    <th style={{ padding: '.4rem', textAlign: 'right' }}>Count</th>
                    <th style={{ padding: '.4rem', textAlign: 'right' }}>Users</th>
                    <th style={{ padding: '.4rem', textAlign: 'right' }}>Avg Time</th>
                  </tr>
                </thead>
                <tbody>
                  {patternList.slice(0, 50).map((p, idx) => {
                    const path = (p.pattern || p.steps || []);
                    return (
                      <tr key={idx} style={{ borderBottom: '1px solid var(--border-light)' }}>
                        <td style={{ padding: '.4rem' }}>{idx + 1}</td>
                        <td style={{ padding: '.4rem' }}>
                          {path.map((step, sIdx) => (
                            <span key={sIdx}>
                              {sIdx > 0 && <span style={{ color: 'var(--text-muted)', margin: '0 .25rem' }}>→</span>}
                              <span style={{
                                display: 'inline-block',
                                padding: '.1rem .4rem',
                                background: COLORS[sIdx % COLORS.length] + '22',
                                color: COLORS[sIdx % COLORS.length],
                                borderRadius: '4px',
                                fontSize: '.65rem',
                                fontWeight: 600
                              }}>
                                {step}
                              </span>
                            </span>
                          ))}
                        </td>
                        <td style={{ padding: '.4rem', textAlign: 'right' }}>{(p.support || p.count || 0).toLocaleString()}</td>
                        <td style={{ padding: '.4rem', textAlign: 'right' }}>{(p.uniqueUsers || p.users || 0).toLocaleString()}</td>
                        <td style={{ padding: '.4rem', textAlign: 'right' }}>
                          {p.avgDurationSeconds != null ? `${Math.round(p.avgDurationSeconds)}s` : p.avgDuration || '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
